import React, {
	ReactNode,
	createContext,
	useEffect,
	useMemo,
	useRef,
	ChangeEvent,
	Provider,
	ReactElement,
	useState,
} from 'react';
import { useDispatch, useSelector as useSel } from 'react-redux';
import {
	ContentServiceJobStatus, ContentTemplate, FkTable, GeneratedContent as GC,
} from '../../stores/reducers/generate-content';
import { GreenroomState as GS } from '../../stores/types/types';
import { Field, InputTypeByCapsuleType, maxPictureCount } from '../../components/right-panel/settings/content-generator-handler';
import { lockedAssetFieldsMedia as LAFM, lockedAssetFieldsOverlay as LAFO } from '../../components/style-packs/style-pack-consts';
import { STATIC_ASSET_URL, contentTemplateStyles } from '../../utils/content-templates';
import { upsertContent } from '../../stores/actions/generate-content';
import { getNumberedFieldName } from '../../components/general-components/input/gr-picture-input';
import useProcessingToaster from '../../hooks/content/use-processing';
import { reorderPictures } from '../../hooks/content/use-drag-content';
import { setShotModalLocked } from '../../stores/actions/shots';
import usePrevious from '../../utils/use-previous';

type ContentGeneratorContextType = {
	statefulFormValues: Record<string, any>;
	selectedGraphicApiShape: Field[];
	currTemplate: ContentTemplate;
	UpdatingToaster: ReactElement<any, Provider<ContentGeneratorContextType>>;
	removePictureWithCount: (field: Field, count: number) => void;
	addNewPicture: (field: Field) => void;
	defaultTitleOverlayData: any;
	onDragEnd: (field: Field, position: number, count: number) => void;
	onChange: (e: ChangeEvent<HTMLInputElement>) => void;
	uniqueId: string;
};

export const FormContext = createContext<ContentGeneratorContextType>({
	statefulFormValues: {},
	selectedGraphicApiShape: [],
	currTemplate: {} as unknown as ContentTemplate,
	UpdatingToaster: <></>,
	removePictureWithCount: () => {},
	addNewPicture: () => {},
	onDragEnd: () => {},
	defaultTitleOverlayData: {},
	onChange: () => {},
	uniqueId: 'no-id',
});

type InputChangeEvent = ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>

type ReturnValue = ReactElement<any, Provider<ContentGeneratorContextType>>

type Props = {
	children: ReactNode
	isForShotEditorModal?: boolean;
}

// a context to hold the state and ref versions of the form outside of the component,
// so that it doesn't lose its value when unrendering the component
// could move more things into this from both title-content.tsx and content-generator-handler.tsx
// as those two components are basically acting as contexts themselves
// however, this was already a big refactor, so I'm leaving it for now
export default function ContentFormProvider(props: Props): ReturnValue {
	const dispatch = useDispatch();
	const { isForShotEditorModal = false } = props;

	const {
		currentGraphicId,
		graphics: overlays,
		shotModalGraphics: overlayDrafts,
	} = useSel((s: GS) => s.GraphicsReducer);

	const {
		contentTemplates,
		generatedContent,
		generatedContentEdits,
		selectedMediaTemplate,
		selectedMediaItem,
	} = useSel((s: GS) => s.GeneratedContentReducer);

	const token = useSel((s: GS) => s.SessionReducer.token);
	const meetingId = useSel((s: GS) => s.SessionReducer.meeting?.meeting);
	const userId = useSel((s: GS) => s.SessionReducer.user?.gr_user);

	const lockedAssetFields = useMemo(
		() => (selectedMediaTemplate ? LAFM : LAFO),
		[selectedMediaTemplate],
	);

	const formSubmitDataRef = useRef<Record<string, any>>({});
	const [statefulFormValues, setStatefulFormValues] = useState<Record<string, any>>({});

	const timmyRef = useRef<NodeJS.Timeout>();

	const usedOverlays = useMemo(
		() => (isForShotEditorModal ? overlayDrafts : overlays),
		[isForShotEditorModal, overlayDrafts, overlays],
	);

	const selectedGraphic = useMemo(
		() => usedOverlays.find((g) => g.id === currentGraphicId),
		[currentGraphicId, usedOverlays],
	);

	const associatedItem = useMemo(
		() => (selectedMediaTemplate ? selectedMediaItem ?? undefined : selectedGraphic),
		[selectedGraphic, selectedMediaItem],
	);

	const currTemplate = useMemo(() => {
		const filteredTemplates = contentTemplates.filter((t) => (selectedMediaTemplate
			? t.type === selectedMediaTemplate
			: t.type === associatedItem?.template_type
		));
		// a variation of a template may have the same type, but a different url
		const templateVariation = filteredTemplates.find((t) => {
			const videoUrl = t?.videoUrl?.replace('ASSET_URL', STATIC_ASSET_URL);
			return !!videoUrl && associatedItem?.images?.includes(videoUrl);
		});
		return templateVariation ?? filteredTemplates[0];
	}, [
		!!selectedMediaTemplate,
		selectedMediaTemplate,
		contentTemplates,
		associatedItem?.template_type,
		associatedItem?.images,
	]);

	const templateHasVariables = useMemo(
		() => (currTemplate?.data?.variables?.length ?? 0) > 0,
		[currTemplate],
	);

	const generatedContentItem = useMemo(() => {
		const cleanFkId = selectedMediaTemplate ? selectedMediaItem?.id : selectedGraphic?.id;
		const cleanFkTable = selectedMediaTemplate ? FkTable.media : FkTable.graphics;
		const backupContent: GC = {
			templateId: currTemplate?.id,
			provider: currTemplate?.provider,
			type: currTemplate?.type,
			fkTable: cleanFkTable,
			fkId: cleanFkId,
			data: [],
			version: 1,
		};

		const match = (gc: GC) => (gc.fkTable === cleanFkTable && gc.fkId === cleanFkId);

		const draftGenContent = generatedContentEdits?.find(match);

		if (isForShotEditorModal && draftGenContent) {
			return draftGenContent;
		}

		return generatedContent?.find(match) ?? backupContent;
	}, [
		isForShotEditorModal,
		generatedContent,
		generatedContentEdits,
		currTemplate,
		selectedMediaTemplate,
		selectedGraphic?.id,
		selectedMediaItem?.id,
	]);

	const selectedGraphicApiShape = useMemo(() => (templateHasVariables && currTemplate
		? currTemplate?.data?.variables?.filter((t) => !['fields', 'compScale'].includes(t.userData)) as Field[]
		: lockedAssetFields.filter((t: any) => !['fields', 'compScale'].includes(t.userData)) as Field[]
	), [currTemplate, templateHasVariables]);

	const uniqueId = useMemo(
		() => `${generatedContentItem.fkTable}-associated:${associatedItem?.id}`,
		[selectedMediaTemplate, associatedItem, generatedContentItem],
	);

	const prevUniqueId = usePrevious(uniqueId);

	const isDirty = useRef<Map<string, Map<string, any>>>(new Map());

	// for internally setting the form, invoked by init, user input change, or context functions
	const hardSetFormValuesRefAndUpdateStateValue = (newValues: Record<string, any>) => {
		formSubmitDataRef.current[uniqueId] = newValues;
		setStatefulFormValues(formSubmitDataRef.current[uniqueId]);
	};

	// a user event has changed an input and we need to update our form
	const handleInputChange = (event: InputChangeEvent) => {
		const { name, value } = event.target;
		formSubmitDataRef.current[uniqueId][name] = value;

		if (!isDirty.current?.get(uniqueId)) {
			isDirty.current?.set(uniqueId, new Map());
		}

		isDirty.current?.get(uniqueId)?.set(name, value);

		hardSetFormValuesRefAndUpdateStateValue(formSubmitDataRef.current[uniqueId]);
	};

	const defaultTitleOverlayData = useMemo(() => {
		const url = currTemplate?.videoUrl?.replace('ASSET_URL', STATIC_ASSET_URL);
		return contentTemplateStyles.find((style) => style.url === url);
	}, [generatedContentItem, currTemplate]);

	const handleFormSubmit = (wUniqueId: string) => {
		if (!token || !meetingId || !userId || !associatedItem?.id || !generatedContentItem) {
			return;
		}

		const usedFormVals = formSubmitDataRef.current[wUniqueId];

		// eslint-disable-next-line no-template-curly-in-string
		const keysToIgnore = ['speaker${i}-headshot', 'speaker${i}-name', 'speaker${i}-company', 'speaker${i}-position'];
		const keys = Object.keys(usedFormVals).filter((key) => !keysToIgnore.includes(key));

		const data = keys.map((key) => ({
			key,
			value: usedFormVals[key],
		}));

		data.push({ key: 'compScale', value: '65' });

		const contentUrl = associatedItem?.id
			? associatedItem.thumbnail_url
			: currTemplate?.data.thumbnail.url;

		if (currTemplate?.type === 'brandlive-walkups') {
			data.push({
				key: 'eventName',
				value: formSubmitDataRef.current[wUniqueId].eventName ?? 'Brandlive Studios',
			});
		}

		// had to set the outgoing version to a timestamp because the UI was sending a stale
		// version in the shot edit modal, and the backend was rejecting it
		const thisVersion = new Date().valueOf();
		const newGeneratedContent = {
			...generatedContentItem,
			fkId: associatedItem?.id,
			data,
			contentUrl,
			jobStatus: ContentServiceJobStatus.processing,
			version: thisVersion,
		};

		if (currTemplate?.data?.apiVersion) {
			newGeneratedContent.apiVersion = currTemplate.data.apiVersion;
		}

		dispatch(setShotModalLocked(newGeneratedContent.version));
		dispatch(upsertContent(
			token,
			meetingId,
			userId,
			associatedItem,
			newGeneratedContent,
		));
	};

	const addNewPicture = (field: Field) => {
		const currentValue = Number(generatedContentItem?.data?.find((d) => d.key === 'pictureCount')?.value
			?? formSubmitDataRef.current[uniqueId]?.pictureCount ?? 1);
		const newValue = currentValue + 1;

		if (newValue > maxPictureCount) {
			return;
		}

		const event = {
			target: {
				name: 'pictureCount',
				value: newValue.toString(),
			},
		} as ChangeEvent<HTMLInputElement>;

		handleInputChange(event);

		const parentFieldName = getNumberedFieldName(field.userData, newValue);
		const defaultParentFieldName = getNumberedFieldName(field.userData, 2);

		const parentValue = {
			target: {
				name: parentFieldName,
				value: defaultTitleOverlayData?.default?.[defaultParentFieldName] ?? '',
			},
		};

		handleInputChange(parentValue as ChangeEvent<HTMLInputElement>);

		field.variables.forEach((variable: any) => {
			const formattedFieldValueName = getNumberedFieldName(variable.userData, 2);
			const formattedFieldName = getNumberedFieldName(variable.userData, newValue);
			const subEvent = {
				target: {
					name: formattedFieldName,
					value: defaultTitleOverlayData?.default?.[formattedFieldValueName] ?? '',
				},
			} as ChangeEvent<HTMLInputElement>;
			handleInputChange(subEvent);
		});

		handleFormSubmit(uniqueId);
	};

	const removePictureWithCount = (field: Field, count: number) => {
		const pictureCount = Number(formSubmitDataRef.current[uniqueId]?.pictureCount ?? 1);
		// we need one picture at least
		if (pictureCount === 1) {
			return;
		}

		const parentFieldName = getNumberedFieldName(field.userData, count);
		const childFieldNames: string[] = field.variables.map(
			(variable: any) => getNumberedFieldName(variable.userData, count),
		);

		const currentValues = { ...formSubmitDataRef.current[uniqueId] };

		const cleanUpValues = (parent: string, children: string[]) => {
			delete currentValues[parent];
			children.forEach((child) => {
				delete currentValues[child];
			});
		};

		// remove current values
		cleanUpValues(parentFieldName, childFieldNames);

		// update and replace data
		for (let i = count + 1; i <= pictureCount; i += 1) {
			const replaceParent = getNumberedFieldName(field.userData, i - 1);
			const replaceChildren = field.variables.map(
				(variable: any) => getNumberedFieldName(variable.userData, i - 1),
			);

			const currentParent = getNumberedFieldName(field.userData, i);
			const currentChildren = field.variables.map(
				(variable: any) => getNumberedFieldName(variable.userData, i),
			);

			// replace current values
			currentValues[replaceParent] = currentValues[currentParent];
			currentChildren.forEach((child: string, index: number) => {
				currentValues[replaceChildren[index]] = currentValues[child];
			});

			// remove current values
			cleanUpValues(currentParent, currentChildren);

			// update the pictureCount
			currentValues.pictureCount = (pictureCount - 1).toString();

			// save updated values
			hardSetFormValuesRefAndUpdateStateValue(currentValues);
			handleFormSubmit(uniqueId);
		}
	};

	const onDragEnd = (field: Field, position: number, count: number) => {
		const newValues = reorderPictures(field, formSubmitDataRef.current[uniqueId], position, count);
		hardSetFormValuesRefAndUpdateStateValue(newValues);
		handleFormSubmit(uniqueId);
	};

	const {
		debounceProcessingToast,
		UpdatingToaster,
	} = useProcessingToaster(
		generatedContentItem?.jobStatus,
		generatedContentItem?.id,
		generatedContentItem?.fkTable === FkTable.media,
	);

	const onChange = (e: ChangeEvent<HTMLInputElement>) => {
		handleInputChange(e);
		clearTimeout(timmyRef.current);
		debounceProcessingToast();
		if (associatedItem) {
			timmyRef.current = setTimeout(() => handleFormSubmit(uniqueId), 800);
		}
	};

	// template can be null ('a:1-g:1-t:null') when loading
	const canShow = useMemo(() => (
		uniqueId.includes('null') === false
		&& selectedGraphicApiShape
		&& associatedItem
		&& currTemplate
		&& statefulFormValues
	), [
		selectedGraphicApiShape,
		associatedItem,
		currTemplate,
		statefulFormValues,
		uniqueId,
	]);

	// determine value of field from existing
	const getField = (eData: Record<string, any>, field: Field, label: string) => (
		eData?.[label]
			?? defaultTitleOverlayData?.default?.[label]?.toString()
			?? field.default?.toString()
			?? '');

	// when we change content, or a version update happens, set displayed form values
	useEffect(() => {
		// when we change which content we're editing, clear the dirty, edited fields
		if (prevUniqueId !== uniqueId) {
			isDirty.current?.set(uniqueId, new Map());
		}

		// grab saved data for overlay (defaults will be used if no data is saved)
		const existingData = generatedContentItem?.data?.reduce((acc, curr) => {
			acc[curr.key] = curr.value;
			return acc;
		}, {} as Record<string, string>);

		const initialFormValues: Record<string, string> = {};

		// map over the api shape and determine the value for the form field
		selectedGraphicApiShape.forEach((field) => {
			if ((field?.variables?.length ?? 0) > 0 && InputTypeByCapsuleType[field.type] === 'picture') {
				const pictureCount = Number(existingData?.pictureCount ?? 1);
				initialFormValues.pictureCount = pictureCount.toString();
				for (let i = 0; i < pictureCount; i += 1) {
					const label = getNumberedFieldName(field.userData, i + 1);
					initialFormValues[label] = getField(existingData, field, label);

					field.variables.forEach((subField: any) => {
						const subLabel = getNumberedFieldName(subField.userData, i + 1);
						initialFormValues[subLabel] = getField(existingData, subField, subLabel);
					});
				}
			} else {
				const label = field.userData;
				initialFormValues[field.userData] = getField(existingData, field, label);
			}
		});

		// do not overwrite values the user has edited since opening this item
		const dirtyFields = isDirty.current?.get(uniqueId);
		dirtyFields?.forEach((value, key) => {
			initialFormValues[key] = value;
		});

		hardSetFormValuesRefAndUpdateStateValue(initialFormValues);
	}, [uniqueId, prevUniqueId, generatedContentItem.data]);

	return (canShow
		? <FormContext.Provider value={{
			statefulFormValues,
			selectedGraphicApiShape,
			currTemplate,
			UpdatingToaster,
			addNewPicture,
			removePictureWithCount,
			defaultTitleOverlayData,
			onDragEnd,
			onChange,
			uniqueId,
		}}>
			{props.children}
		</FormContext.Provider>
		: <></>
	);
}
